import { EditModeColor } from 'features/siteMapViz'
import { FC, RefObject, useCallback, useState } from 'react'
import { MapRef } from 'react-map-gl'
import styled from 'styled-components'
import { GeckoSite } from 'types'

import { SiteBoundsEditorActionButtons } from './SiteBoundsEditorActionButtons'
import { MapboxDrawMode, useMapboxDraw } from './useMapboxDraw'

const Container = styled.div<{ $isOpen: boolean }>`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;

  height: ${({ $isOpen }) => ($isOpen ? '3rem' : '0')};
  width: 100%;
  padding: 0 1rem;

  background-color: ${EditModeColor};
  color: black;
  transition: height 0.1s ease-in-out;
`

const ButtonsContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1rem;
`

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
`

interface SiteBoundsEditorProps {
  mapRef: RefObject<MapRef | null>
  site: GeckoSite
  isUiVisible: boolean
  setSite: (site: GeckoSite) => void
}

/**
 * A component that sets up the ability to define the bounds of a site on a Mapbox map, and displays a helpful UI bar for the user.
 */
export const SiteBoundsEditor: FC<SiteBoundsEditorProps> = (props) => {
  const [canSave, setCanSave] = useState(false)

  const onDrawComplete = useCallback(() => {
    setCanSave(true)
  }, [setCanSave])

  const {
    drawMode,
    setDrawMode,
    clearDrawings,
    getDrawnGeometry,
    putGeometryOnMap,
  } = useMapboxDraw(props.mapRef?.current, onDrawComplete)

  return (
    <Container $isOpen={props.isUiVisible}>
      <TextContainer>
        <span>Site Bounds Editor</span>
        {drawMode === MapboxDrawMode.DRAW_POLYGON && (
          <span>
            Click on the map to draw a polygon. Triple click in an existing
            polygon to select it, and click again to edit.
          </span>
        )}
      </TextContainer>
      <ButtonsContainer>
        <SiteBoundsEditorActionButtons
          canSave={canSave}
          setCanSave={setCanSave}
          site={props.site}
          setSite={props.setSite}
          getDrawnGeometry={getDrawnGeometry}
          clearDrawings={clearDrawings}
          drawMode={drawMode}
          setDrawMode={setDrawMode}
          uploadMapGeometry={putGeometryOnMap}
        />
      </ButtonsContainer>
    </Container>
  )
}
